import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, Loader2, Trash2, Plus, Upload, AlertTriangle, Lock, ImagePlus, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Pill, EmptyState, Field } from "@/components/Bits";

const TABS = ["Portfolio", "Website Clients", "Testimonials", "Site Content", "Media Library"];

const STATUS_TONE = {
  published: "bg-[#BEC221] text-ink",
  draft: "bg-[#EDEFC4] text-ink",
  archived: "bg-line text-stone",
};

export default function ContentStudio() {
  const { user, isStaff } = useAuth();
  // Website content is staff-only; a client-portal user is sent home, like /inquiries.
  if (user && !isStaff) return <Navigate to="/" replace />;
  return <ContentStudioPage isAdmin={user?.role === "admin"} />;
}

function ContentStudioPage({ isAdmin }) {
  const [tab, setTab] = useState("Portfolio");
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/content/overview")
      .then((r) => setOverview(r.data))
      .catch((e) => setError(apiError(e)));
  }, []);

  return (
    <div className="space-y-10" data-testid="content-studio-page">
      <PageHeader
        overline="Content Studio"
        title="Your website"
        subtitle="Portfolio, clients, testimonials and media — edited here, saved straight to asthetik.design."
      />

      {error && (
        <div data-testid="content-error" className="flex items-start gap-3 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-5 py-4 text-sm text-[#A8492E]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span>{error}</span>
        </div>
      )}

      {overview && (
        <div className="card-surface p-6" data-testid="content-overview">
          <div className="mb-5 flex items-center gap-2.5">
            <Globe className="h-4 w-4 text-lime-dark" strokeWidth={1.5} />
            <span className="overline">Public website · not client project data</span>
          </div>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Portfolio", overview.portfolio_total],
              ["Published", overview.portfolio_published],
              ["Drafts", overview.portfolio_draft],
              ["Website clients", overview.website_clients],
              ["Testimonials", overview.testimonials],
              ["Media items", overview.media_items],
            ].map(([label, value]) => (
              <div key={label}>
                <div className="num text-2xl text-ink">{value}</div>
                <div className="overline mt-1 leading-tight">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`content-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`shrink-0 border-b-2 px-4 pb-3 pt-1 text-xs uppercase tracking-[0.12em] transition-colors ${tab === t ? "border-lime text-ink" : "border-transparent text-stone hover:text-ink"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {!isAdmin && (
        <div data-testid="content-readonly-notice" className="flex items-start gap-3 rounded-sm border border-line bg-white px-5 py-4 text-sm text-stone">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-fog" strokeWidth={1.5} />
          <span>
            You can view the website content here. Publishing and editing are limited to the
            studio owner.
          </span>
        </div>
      )}

      {tab === "Portfolio" && <Portfolio isAdmin={isAdmin} />}
      {tab === "Website Clients" && <WebClients isAdmin={isAdmin} />}
      {tab === "Testimonials" && <Testimonials isAdmin={isAdmin} />}
      {tab === "Site Content" && <SiteContent isAdmin={isAdmin} />}
      {tab === "Media Library" && <MediaLibrary isAdmin={isAdmin} />}
    </div>
  );
}

const useList = (path) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const load = () => {
    setError("");
    return api.get(path).then((r) => setRows(r.data)).catch((e) => {
      setRows([]);
      setError(apiError(e));
    });
  };
  useEffect(() => { load(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  return [rows, load, error];
};

const LoadError = ({ message, onRetry }) => (
  <div data-testid="content-load-error" className="card-surface p-7 text-center">
    <AlertTriangle className="mx-auto h-5 w-5 text-[#E07A5F]" strokeWidth={1.5} />
    <p className="mt-4 text-sm leading-relaxed text-ink">{message}</p>
    <p className="mt-2 text-xs text-fog">Nothing has been changed on your website.</p>
    <button data-testid="content-retry-btn" onClick={onRetry}
      className="mt-5 rounded-sm border border-line px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-stone hover:text-ink">
      Try again
    </button>
  </div>
);

function Portfolio({ isAdmin }) {
  const [rows, load, error] = useList("/content/projects");
  const [editing, setEditing] = useState(null);

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.name}” from your website portfolio? This is live.`)) return;
    try {
      await api.delete(`/content/projects/${row.id}`);
      toast.success("Removed from the website");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;
  if (error) return <LoadError message={error} onRetry={load} />;
  return (
    <div className="space-y-5" data-testid="content-portfolio">
      <div className="flex justify-end">
        {isAdmin && <button data-testid="content-add-project" onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> New portfolio project
        </button>}
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="content-portfolio-empty" title="No portfolio projects yet"
          body="Anything you add here appears on asthetik.design." />
      ) : (
        <div className="card-surface divide-y divide-line">
          {rows.map((p) => (
            <div key={p.id} data-testid={`content-project-${p.id}`}
              className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
              <div className="h-16 w-24 shrink-0 overflow-hidden rounded-sm bg-line">
                {p.cover?.url && <img src={p.cover.url} alt={p.name} className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-serif text-lg text-ink">{p.name}</span>
                  <Pill tone={STATUS_TONE[p.status]}>{p.status}</Pill>
                  {p.featured && <Pill tone="bg-[#EDEFC4] text-ink">Featured</Pill>}
                </div>
                <div className="mt-1 text-xs text-fog">
                  {[p.client_name, p.location, p.year].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex shrink-0 gap-3">
                {isAdmin && <button data-testid={`content-edit-${p.id}`} onClick={() => setEditing(p)}
                  className="rounded-sm border border-line px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:text-ink">Edit</button>}
                {isAdmin && <button data-testid={`content-delete-${p.id}`} onClick={() => remove(p)}
                  className="rounded-sm border border-line px-3 py-2 text-stone hover:border-[#E07A5F] hover:text-[#E07A5F]">
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <RecordForm
          title={editing.id ? "Edit portfolio project" : "New portfolio project"}
          testId="content-project-form"
          fields={[
            ["name", "Project name", "text", true],
            ["client_name", "Client name", "text"],
            ["location", "Location", "text"],
            ["year", "Year", "text"],
            ["project_type", "Project type", "text"],
            ["short_description", "Short description", "textarea"],
            ["status", "Status", "select", false, ["draft", "published", "archived"]],
            ["featured", "Feature on the homepage", "checkbox"],
          ]}
          record={editing}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            if (editing.id) await api.put(`/content/projects/${editing.id}`, body);
            else await api.post("/content/projects", body);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function WebClients({ isAdmin }) {
  const [rows, load, error] = useList("/content/clients");
  const [editing, setEditing] = useState(null);

  const remove = async (row) => {
    if (!window.confirm(`Remove “${row.name}” from your website?`)) return;
    try {
      await api.delete(`/content/clients/${row.id}`);
      toast.success("Removed from the website");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;
  if (error) return <LoadError message={error} onRetry={load} />;
  return (
    <div className="space-y-5" data-testid="content-clients">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs leading-relaxed text-fog">
          These are the logos shown on your website — separate from the clients you run projects for.
        </p>
        {isAdmin && <button data-testid="content-add-client" onClick={() => setEditing({})}
          className="inline-flex shrink-0 items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> New
        </button>}
      </div>
      <div className="card-surface divide-y divide-line">
        {rows.map((c) => (
          <div key={c.id} data-testid={`content-webclient-${c.id}`} className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-bone">
              {c.logo?.url && <img src={c.logo.url} alt={c.name} className="max-h-full max-w-full object-contain" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-ink">{c.name}</span>
                {c.feature_on_homepage && <Pill tone="bg-[#BEC221] text-ink">Homepage</Pill>}
              </div>
              <div className="mt-1 text-xs text-fog">{(c.projects || []).length} linked project(s)</div>
            </div>
            {isAdmin && <button data-testid={`content-edit-webclient-${c.id}`} onClick={() => setEditing(c)}
              className="shrink-0 rounded-sm border border-line px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:text-ink">Edit</button>}
            {isAdmin && <button data-testid={`content-delete-webclient-${c.id}`} onClick={() => remove(c)}
              className="shrink-0 rounded-sm border border-line px-3 py-2 text-stone hover:border-[#E07A5F] hover:text-[#E07A5F]">
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>}
          </div>
        ))}
        {rows.length === 0 && <div className="p-10 text-center text-sm text-fog">No website clients yet.</div>}
      </div>
      {editing && (
        <RecordForm
          title={editing.id ? "Edit website client" : "New website client"}
          testId="content-webclient-form"
          fields={[
            ["name", "Name", "text", true],
            ["website", "Website", "text"],
            ["feature_on_homepage", "Show on the homepage", "checkbox"],
          ]}
          record={editing}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            if (editing.id) await api.put(`/content/clients/${editing.id}`, body);
            else await api.post("/content/clients", body);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Testimonials({ isAdmin }) {
  const [rows, load, error] = useList("/content/testimonials");
  const [editing, setEditing] = useState(null);

  const remove = async (row) => {
    if (!window.confirm("Remove this testimonial from your website?")) return;
    try {
      await api.delete(`/content/testimonials/${row.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;
  if (error) return <LoadError message={error} onRetry={load} />;
  return (
    <div className="space-y-5" data-testid="content-testimonials">
      <div className="flex justify-end">
        {isAdmin && <button data-testid="content-add-testimonial" onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> New testimonial
        </button>}
      </div>
      {rows.length === 0 ? (
        <EmptyState testId="content-testimonials-empty" title="No testimonials yet"
          body="Add a client quote and it appears on your website." />
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {rows.map((t) => (
            <div key={t.id} data-testid={`content-testimonial-${t.id}`} className="card-surface p-6">
              <p className="font-serif text-lg leading-relaxed text-ink">“{t.quote || t.text || t.body}”</p>
              <div className="mt-4 text-xs text-fog">
                {[t.client_name || t.name, t.role, t.company].filter(Boolean).join(" · ")}
              </div>
              <div className="mt-5 flex gap-3">
                {isAdmin && <button data-testid={`content-edit-testimonial-${t.id}`} onClick={() => setEditing(t)}
                  className="rounded-sm border border-line px-4 py-2 text-xs uppercase tracking-[0.12em] text-stone hover:text-ink">Edit</button>}
                {isAdmin && <button data-testid={`content-delete-testimonial-${t.id}`} onClick={() => remove(t)}
                  className="rounded-sm border border-line px-3 py-2 text-stone hover:border-[#E07A5F] hover:text-[#E07A5F]">
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <RecordForm
          title={editing.id ? "Edit testimonial" : "New testimonial"}
          testId="content-testimonial-form"
          fields={[
            ["quote", "Quote", "textarea", true],
            ["client_name", "Who said it", "text", true],
            ["role", "Their role", "text"],
            ["company", "Company", "text"],
          ]}
          record={editing}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            if (editing.id) await api.put(`/content/testimonials/${editing.id}`, body);
            else await api.post("/content/testimonials", body);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function SiteContent({ isAdmin }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({ email: "", instagram: "" });
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [hero, setHero] = useState([]);

  const load = () =>
    api.get("/content/site-content").then((r) => {
      setData(r.data);
      setHero((r.data.hero_images || []).map((h) => ({ ...h, file_id: h.id })));
      setSettings({ email: r.data.site_settings?.email || "",
                    instagram: r.data.site_settings?.instagram || "" });
    }).catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/site-content", { site_settings: settings });
      const { data } = await api.get("/content/site-content");
      const ignored = ["email", "instagram"].filter(
        (k) => (settings[k] || "") !== (data.site_settings?.[k] || ""));
      if (ignored.length)
        toast.error(`Your website wouldn’t accept the ${ignored.join(" and ")} — it kept the previous value. Instagram needs a full URL.`);
      else toast.success("Website content updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveHero = async (next) => {
    setHero(next);
    try {
      await api.put("/content/site-content", {
        hero_images: next.map((h) => ({ file_id: h.file_id || h.id,
                                        focal: h.focal || { x: 50, y: 50 } })),
      });
      toast.success("Homepage hero updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  const move = (idx, delta) => {
    const next = [...hero];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    saveHero(next);
  };

  if (!data) return <Loading />;
  const singles = [["founder_image", "Founder"], ["process_image", "Process"],
                   ["about_studio_1", "About studio 1"], ["about_studio_2", "About studio 2"]];

  return (
    <div className="space-y-7" data-testid="content-site">
      <div className="card-surface p-7">
        <h3 className="font-serif text-xl text-ink">Contact details</h3>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field label="Public email">
            <input data-testid="site-email-input" value={settings.email} disabled={!isAdmin}
              onChange={(e) => setSettings({ ...settings, email: e.target.value })}
              className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime disabled:bg-bone disabled:text-fog" />
          </Field>
          <Field label="Instagram">
            <input data-testid="site-instagram-input" value={settings.instagram} disabled={!isAdmin}
              onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
              className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime disabled:bg-bone disabled:text-fog" />
          </Field>
        </div>
        {isAdmin && (
          <button data-testid="site-save-btn" onClick={save} disabled={busy}
            className="mt-6 inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-3 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />} Save to website
          </button>
        )}
      </div>

      <div className="card-surface p-7" data-testid="hero-manager">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl text-ink">Homepage hero</h3>
            <p className="mt-2 text-xs text-fog">
              {hero.length} image(s) in rotation · first one shows first.
            </p>
          </div>
          {isAdmin && (
            <button data-testid="hero-pick-btn" onClick={() => setPicking(true)}
              className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
              <ImagePlus className="h-4 w-4" strokeWidth={1.5} /> Choose images
            </button>
          )}
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="site-hero-images">
          {hero.map((img, idx) => (
            <div key={img.id} data-testid={`hero-slot-${idx}`} className="group relative">
              <div className="aspect-[4/5] overflow-hidden rounded-sm bg-line">
                <img src={img.url} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="absolute left-2 top-2 rounded-sm bg-ink/80 px-2 py-0.5 text-[11px] text-white">{idx + 1}</div>
              {isAdmin && (
                <div className="mt-2 flex items-center justify-between gap-1">
                  <div className="flex gap-1">
                    <button data-testid={`hero-up-${idx}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                      className="rounded-sm border border-line px-2 py-1 text-stone hover:text-ink disabled:opacity-30">
                      <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <button data-testid={`hero-down-${idx}`} onClick={() => move(idx, 1)} disabled={idx === hero.length - 1}
                      className="rounded-sm border border-line px-2 py-1 text-stone hover:text-ink disabled:opacity-30">
                      <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                  <button data-testid={`hero-remove-${idx}`}
                    onClick={() => saveHero(hero.filter((_, i) => i !== idx))}
                    className="rounded-sm border border-line px-2 py-1 text-stone hover:border-[#E07A5F] hover:text-[#E07A5F]">
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          ))}
          {hero.length === 0 && (
            <p className="col-span-full py-6 text-center text-sm text-fog">
              No hero images chosen yet.
            </p>
          )}
        </div>
      </div>

      <div className="card-surface p-7">
        <h3 className="font-serif text-xl text-ink">Section images</h3>
        <div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">
          {singles.map(([key, label]) => (
            <div key={key}>
              <div className="aspect-square overflow-hidden rounded-sm bg-line">
                {data[key]?.url && <img src={data[key].url} alt={label} className="h-full w-full object-cover" />}
              </div>
              <div className="overline mt-2">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {picking && (
        <HeroPicker
          chosen={hero}
          onClose={() => setPicking(false)}
          onSave={(next) => { setPicking(false); saveHero(next); }}
        />
      )}
    </div>
  );
}

function HeroPicker({ chosen, onClose, onSave }) {
  const [media, , error] = useList("/content/media");
  const [picked, setPicked] = useState(chosen.map((h) => h.file_id || h.id));

  const toggle = (id) =>
    setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose}
      data-testid="hero-picker">
      <div onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-2xl flex-col bg-bone">
        <div className="flex items-start justify-between gap-6 p-7 sm:p-10 sm:pb-6">
          <div>
            <div className="overline">Homepage hero</div>
            <h2 className="mt-2 font-serif text-2xl tracking-tight text-ink">Choose images</h2>
            <p className="mt-2 text-xs text-fog">
              Tap to select. They appear in the order you pick them — reorder afterwards.
            </p>
          </div>
          <button data-testid="hero-picker-close" onClick={onClose}
            className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto px-7 sm:px-10">
          {error ? (
            <p className="py-10 text-center text-sm text-[#A8492E]">{error}</p>
          ) : !media ? (
            <Loading />
          ) : (
            <div className="grid grid-cols-3 gap-3 pb-6 sm:grid-cols-4">
              {media.map((m) => {
                const order = picked.indexOf(m.id);
                return (
                  <button key={m.id} type="button" data-testid={`hero-option-${m.id}`}
                    onClick={() => toggle(m.id)}
                    className={`relative aspect-square overflow-hidden rounded-sm border-2 transition-colors ${order >= 0 ? "border-lime" : "border-transparent hover:border-line"}`}>
                    <img src={m.url} alt={m.filename} loading="lazy" className="h-full w-full object-cover" />
                    {order >= 0 && (
                      <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-lime text-[11px] font-medium text-ink">
                        {order + 1}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t border-line bg-white p-5 sm:px-10">
          <button data-testid="hero-picker-save" onClick={() => onSave(
              picked.map((id) => ({ file_id: id, focal: { x: 50, y: 50 } })))}
            className="w-full rounded-sm bg-lime px-6 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
            Save {picked.length} image(s) to the homepage
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function MediaLibrary({ isAdmin }) {
  const [rows, load, error] = useList("/content/media");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const body = new FormData();
    files.forEach((f) => body.append("files", f));
    try {
      await api.post("/content/media", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} file(s) uploaded to the website`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Remove this file from the website media library?")) return;
    try {
      await api.delete(`/content/media/${item.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;
  if (error) return <LoadError message={error} onRetry={load} />;
  return (
    <div className="space-y-5" data-testid="content-media">
      {isAdmin && <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Upload className="h-4 w-4" strokeWidth={1.5} />}
        Upload images
        <input data-testid="media-upload-input" type="file" multiple accept="image/*" onChange={upload} className="hidden" />
      </label>}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6" data-testid="media-grid">
        {rows.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="group relative aspect-square overflow-hidden rounded-sm bg-line">
            <img src={m.url} alt={m.filename} loading="lazy" className="h-full w-full object-cover" />
            <button
              data-testid={`media-delete-${m.id}`}
              onClick={() => remove(m)}
              className="absolute right-2 top-2 rounded-sm bg-ink/80 p-1.5 text-white transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>}
          </div>
        ))}
      </div>
      {rows.length === 0 && <EmptyState testId="media-empty" title="Nothing in the library yet" body="Upload images to use across your website." />}
    </div>
  );
}

function RecordForm({ title, fields, record, onClose, onSave, testId }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(fields.map(([k, , type]) => [k, record[k] ?? (type === "checkbox" ? false : "")])));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(form);
      toast.success("Saved to your website");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose} data-testid={testId}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="h-full w-full max-w-lg overflow-y-auto bg-bone p-7 sm:p-10">
        <div className="flex items-start justify-between gap-6">
          <h2 className="font-serif text-2xl tracking-tight text-ink">{title}</h2>
          <button type="button" data-testid={`${testId}-close`} onClick={onClose}
            className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Close</button>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-fog">
          Saving publishes straight to asthetik.design.
        </p>
        <div className="mt-7 space-y-5">
          {fields.map(([key, label, type = "text", required, options]) => (
            <Field key={key} label={label}>
              {type === "textarea" ? (
                <textarea data-testid={`field-${key}`} rows={4} required={required} value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
              ) : type === "select" ? (
                <select data-testid={`field-${key}`} value={form[key] || options[0]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime">
                  {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : type === "checkbox" ? (
                <label className="flex items-center gap-3 text-sm text-ink">
                  <input data-testid={`field-${key}`} type="checkbox" checked={!!form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                    className="h-4 w-4 accent-[#BEC221]" />
                  Yes
                </label>
              ) : (
                <input data-testid={`field-${key}`} required={required} value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
              )}
            </Field>
          ))}
        </div>
        <button data-testid={`${testId}-submit`} type="submit" disabled={busy}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-sm bg-lime px-6 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />} Save
        </button>
      </form>
    </div>
  );
}

const Loading = () => (
  <div className="flex items-center justify-center gap-3 py-16 text-sm text-fog">
    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading from your website…
  </div>
);

